import { useCallback, useState } from "react";

import {
  type FormulaTraitConfig,
  TraitType,
} from "@hightouch/lib/query/visual/types/trait-definitions";
import type {
  RelatedColumn,
  TransformedColumn,
} from "@hightouch/lib/query/visual/types";
import { TRAIT_PREVIEW_ALIAS } from "@hightouch/lib/query/visual/util";
import { useToast } from "@hightouch/htui";

import { useVisualQuerySqlQuery } from "src/graphql";

import type { PreviewableTrait } from "src/components/traits/use-preview-trait";

type AudienceSubset = {
  subset_value: {
    id: string;
  };
};

export const useTraitSqlPreview = ({
  parentModel,
  trait,
  subsets,
  useSampledModels,
}: {
  parentModel: {
    id: string;
    connection?: { id: string } | null;
  };
  trait: PreviewableTrait;
  subsets?: AudienceSubset[];
  useSampledModels?: boolean;
}) => {
  const { toast } = useToast();

  const traitColumn: RelatedColumn | TransformedColumn =
    // Tracked-audience-membership traits have no relationship; reference the
    // saved trait definition directly with an empty join path so the backend
    // reads the audience_membership changelog.
    trait.type === TraitType.TrackedAudienceMembership
      ? {
          type: "related",
          column: {
            type: "trait",
            traitDefinitionId: trait.traitDefinitionId,
            conditions: [],
          },
          path: [],
        }
      : trait.type !== TraitType.Formula && trait.relationshipId
        ? {
            type: "related",
            column: {
              type: "inline_trait",
              traitType: trait.type,
              traitConfig: trait.config,
              conditions: trait.conditions ?? [],
              relationshipId: trait.relationshipId,
            },
            path: [trait.relationshipId],
          }
        : {
            type: "transformed",
            column: {
              type: "inline_trait",
              traitType: trait.type as TraitType.Formula,
              traitConfig: trait.config as FormulaTraitConfig,
            },
          };

  const subsetIds = subsets?.map((subset) => subset.subset_value.id) ?? [];

  const visualQueryFilter = {
    conditions: [],
    additionalColumns: [
      {
        alias: TRAIT_PREVIEW_ALIAS,
        column: traitColumn,
      },
    ],
  };

  const { refetch: refetchVisualQuerySql } = useVisualQuerySqlQuery(
    {
      connection_id: parentModel.connection?.id?.toString() ?? "",
      parent_model_id: parentModel.id?.toString() ?? "",
      filter: visualQueryFilter,
      // Workspaces with required subset groups reject the query without them.
      // The backend applies default subsets only when subset_ids is omitted.
      subset_ids: subsetIds.length ? subsetIds : undefined,
      useDefaultSubsets: !subsetIds.length,
      useSampledModels,
    },
    {
      enabled: false,
      meta: { isFastEndpoint: true },
    },
  );

  const queryKey = JSON.stringify({
    filter: visualQueryFilter,
    subsetIds,
    useSampledModels,
  });

  const [transformedSql, setTransformedSql] = useState<{
    sql: string | undefined | null;
    queryKey: string | null;
    loading: boolean;
    visible: boolean;
  }>({
    sql: undefined,
    queryKey: null,
    loading: false,
    visible: false,
  });

  const showSql = useCallback(async () => {
    if (transformedSql.sql && transformedSql.queryKey === queryKey) {
      setTransformedSql((state) => ({ ...state, visible: true }));
      return;
    }

    setTransformedSql((state) => ({
      ...state,
      loading: true,
      visible: true,
    }));
    try {
      const result = await refetchVisualQuerySql({ throwOnError: true });
      const sql = result.data?.visualQuerySQL?.sql;
      if (!sql) {
        throw new Error(
          "Couldn't load SQL for this trait. Try again, or contact support if it keeps happening.",
        );
      }
      setTransformedSql((state) => ({
        ...state,
        sql,
        queryKey,
        loading: false,
      }));
    } catch (error) {
      setTransformedSql((state) => ({
        ...state,
        loading: false,
        visible: false,
      }));
      toast({
        id: "sql-preview",
        title: "SQL preview failed",
        message: error instanceof Error ? error.message : "Unknown error",
        variant: "error",
      });
    }
  }, [
    refetchVisualQuerySql,
    toast,
    queryKey,
    transformedSql.sql,
    transformedSql.queryKey,
  ]);

  const hideSql = useCallback(() => {
    setTransformedSql((state) => ({ ...state, visible: false }));
  }, []);

  const copySql = useCallback(() => {
    if (transformedSql.sql) {
      navigator.clipboard.writeText(transformedSql.sql);
      toast({
        id: "copy-sql",
        title: "SQL copied to clipboard",
        variant: "success",
      });
    }
  }, [transformedSql.sql, toast]);

  return {
    sql: transformedSql.sql,
    loading: transformedSql.loading,
    visible: transformedSql.visible,
    showSql,
    hideSql,
    copySql,
  };
};
